import { AsyncPipe, DecimalPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, effect, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatRipple } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Store, select } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { TeamSettingsModel } from 'models/settings';
import { PlayerStatisticsCollectionModel, PlayerStatisticsModel } from 'models/statistics';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { InviteModalComponent } from 'shared/invite-modal/invite-modal.component';
import { AppState, SeasonStore, playersActions } from 'store';
import { selectSettings } from 'store/players/players.selectors';
import { AvatarUrlPipe } from '../shared/pipes/avatar-url.pipe';
import { IPlayerWinRate } from '../store/players/players.model';
import { PlayerPerformanceComponent } from './player-performance/player-performance.component';

@Component({
    selector: 'app-players',
    templateUrl: './players.component.html',
    styleUrls: ['./players.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatButton,
        MatIcon,
        MatRipple,
        PlayerPerformanceComponent,
        MatTooltip,
        NgClass,
        TranslateModule,
        AsyncPipe,
        DecimalPipe,
        AvatarUrlPipe,
    ],
})
export class PlayersComponent implements OnInit, OnDestroy {
    private readonly store = inject<Store<AppState>>(Store);
    private readonly matDialog = inject(MatDialog);
    private readonly seasonStore = inject(SeasonStore);

    public players$: Observable<Array<PlayerStatisticsCollectionModel>>;
    public detail$: Observable<PlayerStatisticsModel>;
    public detailId$: Observable<number>;
    private readonly _unsubscribe = new Subject<void>();
    public activePlayer: number = null;

    public sortCol: keyof IPlayerWinRate = 'displayName';
    public sortColAsc = true;

    public teamSettings: TeamSettingsModel;

    constructor() {
        effect(() => {
            const season = this.seasonStore.active();
            this.store.dispatch(playersActions.loadPlayersStats({ season: season }));
            if (this.activePlayer) {
                this.store.dispatch(playersActions.loadPlayersStatsDetail({ idPlayer: this.activePlayer, season: season }));
            }
        });
    }

    ngOnInit() {
        this.store
            .select(selectSettings)
            .pipe(takeUntil(this._unsubscribe))
            .subscribe((settings) => {
                this.teamSettings = settings;
            });
        this.players$ = this.store.pipe(select((x) => x.players.stats));
        this.detail$ = this.store.pipe(
            select((x) => x.players.statsDetail),
            tap((x) => (this.activePlayer = x ? x.id : null)),
        );
    }

    ngOnDestroy() {
        this._unsubscribe.next();
        this._unsubscribe.complete();
    }

    showDetail(player: PlayerStatisticsCollectionModel) {
        if (player.id !== this.activePlayer) {
            this.store.dispatch(playersActions.loadPlayersStatsDetail({ idPlayer: player.id, season: this.seasonStore.active() }));
        }
        this.activePlayer = null;
    }

    public sortWinRate($event: MouseEvent, col: keyof IPlayerWinRate) {
        $event.stopPropagation();
        if (this.sortCol === col) {
            this.sortColAsc = !this.sortColAsc;
        } else {
            this.sortColAsc = true;
        }
        this.sortCol = col;
        this.store.dispatch(playersActions.sortWinRate({ col: col, asc: this.sortColAsc }));
    }

    public getSortClass(col: keyof IPlayerWinRate) {
        if (this.sortCol !== col) {
            return ['fa-sort', 'text-muted'];
        } else if (this.sortColAsc) {
            return ['fa-sort-up'];
        } else {
            return ['fa-sort-down'];
        }
    }

    public openInviteModal() {
        this.matDialog
            .open(InviteModalComponent, { autoFocus: false })
            .afterClosed()
            .subscribe(() => {
                this.store.dispatch(playersActions.loadPlayers({ force: true }));
                const activeSeason = this.seasonStore.active();

                this.store.dispatch(playersActions.loadPlayersStats({ season: activeSeason }));
                if (this.activePlayer) {
                    this.store.dispatch(
                        playersActions.loadPlayersStatsDetail({ idPlayer: this.activePlayer, season: this.seasonStore.active() }),
                    );
                }
            });
    }
}
